import {
	type ActionItem,
	CALL_STATUS,
	type Caller,
	COMPANY_DAILY_STATS_BUCKET_ID,
	type DailyCallStats,
	isCallStatus,
} from "@repo/common";
import {
	auditLogsCollection,
	auth,
	createService as createServiceDocument,
	db,
	deleteService as deleteServiceDocument,
	functions,
	httpsCallable,
	logAction,
	serviceTagsCollection,
	subscriptionsCollection,
	touchUser,
} from "@repo/ui";
import * as Sentry from "@sentry/react";
import {
	onAuthStateChanged,
	signInWithEmailAndPassword,
	signOut,
} from "firebase/auth";
import type { DocumentData } from "firebase/firestore";
import {
	arrayUnion,
	collection,
	doc,
	limit,
	onSnapshot,
	or,
	orderBy,
	query,
	runTransaction,
	serverTimestamp,
	setDoc,
	Timestamp,
	where,
} from "firebase/firestore";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { toast } from "sonner";
import { activateLocale } from "../i18n";
import {
	addOrUpdateDocument,
	removeDocument,
	resetSearchIndex,
	type SearchDocument,
} from "../search/searchIndex";
import type {
	Agent,
	AppUser,
	AuditLog,
	Call,
	Company,
	DeploymentStatus,
	Service,
	ServiceNotificationSettings,
	ServiceTag,
	Subscription,
	User,
} from "../types";
import { mapCallParticipantData } from "../utils/callParticipants";
import { normalizePhone } from "../utils/normalizePhone";
import { reloadApp } from "../utils/reloadApp";
import { normalizeTagValue } from "../utils/tagValues";
import { selectDisplayTranscript } from "../utils/transcript";

type Language = "en" | "he";

const DEFAULT_NOTIFICATION_FROM_NAME = "{{ service.name }} - Agent";
const DEFAULT_NOTIFICATION_SUBJECT =
	"New call from {{ callerNumber }} about {{ callSubject }}";
const DEFAULT_NOTIFICATION_BODY =
	"A new call from {{ callerNumber }} about {{ callSubject }} was just handled.\n\n{{ linkToCall }}";

function defaultServiceNotifications(): ServiceNotificationSettings {
	return {
		enabled: true,
		emails: [],
		fromName: DEFAULT_NOTIFICATION_FROM_NAME,
		subject: DEFAULT_NOTIFICATION_SUBJECT,
		body: DEFAULT_NOTIFICATION_BODY,
	};
}

function normalizeServiceNotifications(
	value: unknown,
): ServiceNotificationSettings {
	const defaults = defaultServiceNotifications();
	if (!value || typeof value !== "object" || Array.isArray(value)) {
		return defaults;
	}

	const data = value as Partial<ServiceNotificationSettings>;
	return {
		enabled:
			typeof data.enabled === "boolean" ? data.enabled : defaults.enabled,
		emails: Array.isArray(data.emails)
			? data.emails.filter(
					(email): email is string => typeof email === "string",
				)
			: defaults.emails,
		fromName:
			typeof data.fromName === "string" && data.fromName.trim()
				? data.fromName
				: defaults.fromName,
		subject:
			typeof data.subject === "string" && data.subject.trim()
				? data.subject
				: defaults.subject,
		body:
			typeof data.body === "string" && data.body.trim()
				? data.body
				: defaults.body,
	};
}

type PreviewGeneratedFile = {
	path: string;
	content: string;
};
type PreviewGenerateAgentFilesResult = {
	agentId: string;
	version: number;
	generatedAt: string;
	files: PreviewGeneratedFile[];
};

interface DataContextType {
	// Auth
	user: AppUser | null;
	isLoggedIn: boolean;
	login: (email: string, password: string) => Promise<void>;
	logout: () => Promise<void>;
	loading: boolean;

	// Language
	language: Language;
	setLanguage: (language: Language) => void;

	// Data
	agents: Agent[];
	testingPhoneNumbers: string[];
	services: Service[];
	/** All service tags across all services — from subcollections, not document fields. */
	serviceTags: ServiceTag[];
	calls: Call[];
	callsLoading: boolean;
	callsError: boolean;
	dashboardRecentCalls: Call[];
	actionItems: ActionItem[];
	auditLogs: AuditLog[];
	/** Flat list of agent and company-level callStatsDaily documents, last 365 days. */
	dailyCallStats: DailyCallStats[];

	// Company
	companyProfile: Company | null;
	subscription: Subscription | null;
	users: Array<User & { uid: string }>;
	updateCompanyProfile: (data: Partial<Company>) => Promise<void>;
	updateCompanyUser: (userId: string, data: Partial<User>) => Promise<void>;
	deleteCompanyUser: (userId: string) => Promise<void>;

	// Agent methods
	updateAgent: (agent: Agent) => Promise<void>;
	createAgent: (agent: Agent) => Promise<void>;
	updateService: (service: Service) => Promise<void>;
	createService: (service: Service) => Promise<void>;
	deleteService: (serviceId: string) => Promise<void>;
	deleteAgent: (agentId: string) => Promise<void>;
	addPhoneNumber: (agentId: string, phoneNumber: string) => Promise<void>;
	removePhoneNumber: (agentId: string, phoneNumber: string) => Promise<void>;
	updateUserProfile: (data: Partial<User>) => Promise<void>;
	refreshUser: () => Promise<void>;

	// New Agent Methods
	promoteToTesting: (agentId: string) => Promise<void>;
	promoteToLive: (agentId: string) => Promise<void>;
	rollbackLive: (agentId: string, targetVersion?: number) => Promise<void>;
	previewGenerateAgentFiles: (
		agentId: string,
	) => Promise<PreviewGenerateAgentFilesResult>;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

function normalizeDeploymentSummary(
	deployment: unknown,
): Agent["stagingDeployment"] | undefined {
	if (!deployment || typeof deployment !== "object") return undefined;

	const data = deployment as Record<string, unknown>;
	const createdAtValue = data.createdAt;
	const createdAt =
		createdAtValue instanceof Timestamp
			? createdAtValue.toDate().getTime()
			: typeof createdAtValue === "number"
				? createdAtValue
				: undefined;
	const updatedAtValue = data.updatedAt;
	const updatedAt =
		updatedAtValue instanceof Timestamp
			? updatedAtValue.toDate().getTime()
			: typeof updatedAtValue === "number"
				? updatedAtValue
				: undefined;
	const serviceUpdatedAtValue = data.serviceUpdatedAt;
	const serviceUpdatedAt =
		serviceUpdatedAtValue instanceof Timestamp
			? serviceUpdatedAtValue.toDate().getTime()
			: typeof serviceUpdatedAtValue === "number"
				? serviceUpdatedAtValue
				: null;

	return {
		...(data as unknown as Agent["stagingDeployment"]),
		id: typeof data.id === "string" ? data.id : "",
		createdAt,
		updatedAt,
		serviceUpdatedAt,
	};
}

function formatLocalDateKey(date: Date): string {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
}

export function DataProvider({ children }: { children: ReactNode }) {
	// Initialize language from localStorage or default to 'en'
	const [language, setLanguageState] = useState<Language>(() => {
		const savedLanguage = localStorage.getItem("language");
		const lang =
			savedLanguage === "he" || savedLanguage === "en" ? savedLanguage : "en";
		activateLocale(lang);
		return lang;
	});

	const [user, setUser] = useState<AppUser | null>(null);
	const [loading, setLoading] = useState(true);

	// Real Data State
	const [calls, setCalls] = useState<Call[]>([]);
	const [callsLoading, setCallsLoading] = useState(false);
	const [callsError, setCallsError] = useState(false);
	const [agents, setAgents] = useState<Agent[]>([]);
	const [testingPhoneNumbers, setTestingPhoneNumbers] = useState<string[]>([]);
	const [services, setServices] = useState<Service[]>([]);
	const [companyProfile, setCompanyProfile] = useState<Company | null>(null);
	const [subscription, setSubscription] = useState<Subscription | null>(null);
	const [users, setUsers] = useState<Array<User & { uid: string }>>([]);
	const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
	const [dailyCallStats, setDailyCallStats] = useState<DailyCallStats[]>([]);
	const dailyStatsUnsubscribersRef = useRef<Map<string, () => void>>(new Map());
	const dailyStatsDocsRef = useRef<Map<string, DailyCallStats[]>>(new Map());
	const [serviceTags, setServiceTags] = useState<ServiceTag[]>([]);
	const serviceTagsUnsubscribersRef = useRef<Map<string, () => void>>(
		new Map(),
	);
	const serviceTagsDocsRef = useRef<Map<string, ServiceTag[]>>(new Map());
	const deploymentUnsubscribersRef = useRef(
		new Map<
			string,
			{
				staging?: () => void;
				production?: () => void;
			}
		>(),
	);
	const accountListenerCleanupsRef = useRef(new Set<() => void>());
	const activeAccountRef = useRef<{ uid: string; companyId?: string } | null>(
		null,
	);
	const trackAccountListenerCleanup = useCallback((cleanup: () => void) => {
		let active = true;
		const stop = () => {
			if (!active) return;
			active = false;
			cleanup();
			accountListenerCleanupsRef.current.delete(stop);
		};
		accountListenerCleanupsRef.current.add(stop);
		return stop;
	}, []);
	const clearServiceTagSubscriptions = useCallback(() => {
		serviceTagsUnsubscribersRef.current.forEach((unsubscribe) => unsubscribe());
		serviceTagsUnsubscribersRef.current.clear();
		serviceTagsDocsRef.current.clear();
		setServiceTags([]);
	}, []);

	const [actionItems, setActionItems] = useState<ActionItem[]>([]);
	const clearAccountData = useCallback(() => {
		accountListenerCleanupsRef.current.forEach((stop) => stop());
		dailyStatsUnsubscribersRef.current.forEach((stop) => stop());
		dailyStatsUnsubscribersRef.current.clear();
		dailyStatsDocsRef.current.clear();
		clearServiceTagSubscriptions();
		deploymentUnsubscribersRef.current.forEach(({ staging, production }) => {
			staging?.();
			production?.();
		});
		deploymentUnsubscribersRef.current.clear();
		resetSearchIndex();
		setCalls([]);
		setCallsLoading(false);
		setCallsError(false);
		setAgents([]);
		setTestingPhoneNumbers([]);
		setServices([]);
		setCompanyProfile(null);
		setSubscription(null);
		setUsers([]);
		setAuditLogs([]);
		setDailyCallStats([]);
		setActionItems([]);
	}, [clearServiceTagSubscriptions]);

	// Wrapper for language updates with per-user persistence
	const setLanguage = (newLanguage: Language) => {
		activateLocale(newLanguage);
		setLanguageState(newLanguage); // update UI
		if (!user) {
			localStorage.setItem("language", newLanguage); // local storage only
			return;
		}

		updateUserProfile({ language: newLanguage }) // cloud storage
			.then(() => {
				localStorage.setItem("language", newLanguage); // local storage
				toast.success("Language preference updated");
			})
			.catch((error) => {
				console.error("Failed to save language preference", error);
				toast.error("Language changed for this session only");
			});
	};

	// Auth Effect
	useEffect(() => {
		let authVersion = 0;
		const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
			const version = ++authVersion;
			if (firebaseUser) {
				let accountChanged = false;
				if (
					activeAccountRef.current &&
					activeAccountRef.current.uid !== firebaseUser.uid
				) {
					clearAccountData();
					activeAccountRef.current = null;
					setUser(null);
					accountChanged = true;
				}
				const tokenResult = await firebaseUser.getIdTokenResult(true);
				if (version !== authVersion) return;
				// Parse the new multi-tenant claims structure
				const companyId = tokenResult.claims.companyId as string | undefined;
				if (
					activeAccountRef.current?.uid !== firebaseUser.uid ||
					activeAccountRef.current.companyId !== companyId
				) {
					if (activeAccountRef.current !== null) {
						accountChanged = true;
						clearAccountData();
					} else if (!accountChanged) {
						clearAccountData();
					}
					activeAccountRef.current = { uid: firebaseUser.uid, companyId };
				}
				if (accountChanged) reloadApp();
				const systemAdmin = tokenResult.claims.systemAdmin === true;
				const companyAdmin =
					(tokenResult.claims.companyAdmin as string[]) || [];

				let role: "user" | "admin" | "system-admin" = "user";
				if (systemAdmin) {
					role = "system-admin";
				} else if (companyId && companyAdmin.includes(companyId)) {
					role = "admin";
				}

				if (!companyId && !systemAdmin) {
					setUser(null);
					setLoading(false);
					return;
				}

				setUser({
					uid: firebaseUser.uid,
					email: firebaseUser.email,
					displayName: firebaseUser.displayName,
					photoURL: firebaseUser.photoURL,
					companyId: companyId, // Might be undefined if not set
					role: role,
				});

				if (companyId) {
					// Update lastLogin
					// We cast to any because touchUser only needs companyId but types requires full object
					touchUser(db, firebaseUser.uid, { companyId }).catch((err) =>
						console.error("Failed to update lastLogin", err),
					);

					logAction(db, {
						userId: firebaseUser.uid,
						type: "user_login",
						companyId: companyId,
					}).catch((err) => console.error("Failed to log login", err));
				}
			} else {
				if (activeAccountRef.current !== null) {
					clearAccountData();
					activeAccountRef.current = null;
					reloadApp();
				}
				setUser(null);
			}
			setLoading(false);
		});

		return () => {
			authVersion++;
			unsubscribe();
		};
	}, [clearAccountData]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: Account and company changes must clear the shared search index, including sign-out.
	useEffect(() => {
		resetSearchIndex();
	}, [user?.companyId, user?.uid]);

	useEffect(() => {
		if (!user?.uid) {
			setTestingPhoneNumbers([]);
			return;
		}

		let cancelled = false;
		const listTestingNumbers = httpsCallable<
			Record<string, never>,
			{ phoneNumbers: string[] }
		>(functions, "listActiveTestingPhoneNumbers");

		listTestingNumbers({})
			.then(({ data }) => {
				if (!cancelled) setTestingPhoneNumbers(data.phoneNumbers);
			})
			.catch((error) => {
				if (cancelled) return;
				setTestingPhoneNumbers([]);
				console.error("Failed to load testing phone numbers", error);
				Sentry.captureException(error);
			});

		return () => {
			cancelled = true;
		};
	}, [user?.uid]);

	// Company and User Profile Data Fetching Effect
	useEffect(() => {
		if (!user?.companyId || !user?.uid) return;

		// 1. Listen to Company Document
		const companyRef = doc(db, "companies", user.companyId);
		const unsubscribeCompany = onSnapshot(companyRef, (doc) => {
			if (doc.exists()) {
				const data = doc.data();
				setUser((prev) => (prev ? { ...prev, companyName: data.name } : null));
				setCompanyProfile({ ...data, id: doc.id } as Company);
			}
		});

		// 2. Listen to User Profile Document
		const userProfileRef = doc(
			db,
			"companies",
			user.companyId,
			"users",
			user.uid,
		);
		const unsubscribeUserProfile = onSnapshot(userProfileRef, (doc) => {
			if (doc.exists()) {
				const data = doc.data();
				const dbLanguage = data.language as Language | undefined;

				if (dbLanguage && (dbLanguage === "en" || dbLanguage === "he")) {
					if (dbLanguage !== language) {
						setLanguageState(dbLanguage);
						localStorage.setItem("language", dbLanguage);
					}
				}

				setUser((prev) =>
					prev
						? {
								...prev,
								displayName: data.displayName || data.name || prev.displayName,
								phoneNumber:
									data.phoneNumber !== undefined
										? data.phoneNumber
										: prev.phoneNumber,
								language: dbLanguage,
								// Ensure we keep other fields
							}
						: null,
				);
			}
		});

		// 3. Listen to All Company Users
		const usersRef = collection(db, "companies", user.companyId, "users");
		const unsubscribeUsers = onSnapshot(usersRef, (snapshot) => {
			const fetchedUsers = snapshot.docs.map((doc) => {
				const data = doc.data();
				return {
					...data,
					uid: doc.id,
					displayName: data.displayName || data.name,
				} as User & { uid: string };
			});
			setUsers(fetchedUsers);
		});

		// 4. Listen to Subscription Document for this company
		const subQuery = query(
			subscriptionsCollection(db),
			where("companyId", "==", user.companyId),
			where("status", "in", ["active", "trial", "past_due"]),
			limit(1),
		);
		const unsubscribeSubscription = onSnapshot(subQuery, (snapshot) => {
			if (!snapshot.empty) {
				setSubscription(snapshot.docs[0].data());
			} else {
				setSubscription(null);
			}
		});

		return trackAccountListenerCleanup(() => {
			unsubscribeCompany();
			unsubscribeUserProfile();
			unsubscribeUsers();
			unsubscribeSubscription();
		});
	}, [user?.companyId, user?.uid, language, trackAccountListenerCleanup]);

	// Audit Logs Fetching Effect
	useEffect(() => {
		if (
			!user?.companyId ||
			!user.uid ||
			(user.role !== "admin" && user.role !== "system-admin")
		) {
			setAuditLogs([]);
			return;
		}

		const q = query(
			auditLogsCollection(db),
			where("companyId", "==", user.companyId),
			orderBy("timestamp", "desc"),
			limit(100),
		);

		const unsubscribe = onSnapshot(q, (snapshot) => {
			const fetchedLogs = snapshot.docs.map((doc) => ({
				...doc.data(),
				id: doc.id,
			})) as AuditLog[];
			setAuditLogs(fetchedLogs);
		});

		return trackAccountListenerCleanup(unsubscribe);
	}, [user?.companyId, user?.uid, user?.role, trackAccountListenerCleanup]);
	// array to prevent unnecessary Firestore listener teardown/re-subscribe cycles on language toggles.

	// Calls Data Fetching Effect
	useEffect(() => {
		if (!user?.companyId || !user.uid) {
			setCallsLoading(false);
			setCallsError(false);
			return;
		}

		setCallsLoading(true);
		setCallsError(false);

		// 1. Listen to Calls
		const callsRef = collection(db, "companies", user.companyId, "calls");
		const qCalls = query(
			callsRef,
			or(
				where("isPiiRedacted", "==", true),
				where("piiProcessing.detection", "==", "disabled"),
			),
			orderBy("createdAt", "desc"),
		);

		// 2. Listen to Action Items
		const actionItemsRef = collection(
			db,
			"companies",
			user.companyId,
			"actionItems",
		);
		const qActionItems = query(actionItemsRef);

		// 3. Listen to Callers
		const callersRef = collection(db, "companies", user.companyId, "callers");
		const qCallers = query(callersRef);

		// 4. Listen to Agents
		const agentsRef = collection(db, "companies", user.companyId, "agents");
		const qAgents = query(agentsRef);
		const servicesRef = collection(db, "companies", user.companyId, "services");
		const qServices = query(servicesRef, orderBy("name", "asc"));

		let localReleasedCalls: Call[] = [];
		let localPendingCalls: Call[] = [];
		let releasedCallsReady = false;
		let pendingCallsReady = false;
		let callsListenerFailed = false;
		const updateCallsLoadState = () => {
			if (callsListenerFailed) {
				setCallsLoading(false);
				setCallsError(true);
			} else if (releasedCallsReady && pendingCallsReady) {
				setCallsLoading(false);
				setCallsError(false);
			}
		};
		let localActionItems: ActionItem[] = [];
		let localCallers: Caller[] = [];
		// Agents are handled separately as they don't need merging logic with calls usually
		// But we need to listen to them.

		const buildCall = (doc: { id: string; data: () => DocumentData }) => {
			const data = doc.data();
			const participantData = mapCallParticipantData(data);
			const dateObj =
				data.occurredAt?.toDate() ||
				(data.dateCreated ? new Date(data.dateCreated) : null) ||
				data.createdAt?.toDate() ||
				new Date();
			const date = formatLocalDateKey(dateObj);

			const hours = dateObj.getHours();
			const minutes = dateObj.getMinutes();
			const time = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;

			const transcriptEdit = data.transcriptEdit ?? null;
			const generatedTranscript = selectDisplayTranscript({
				formattedTranscript: data.formattedTranscript,
				transcriptEdit,
				isPiiRedacted: data.isPiiRedacted,
				piiProcessing: data.piiProcessing,
			});

			return {
				id: doc.id,
				...participantData,
				serviceId: data.serviceId || "",
				caseId: typeof data.caseId === "number" ? data.caseId : undefined,
				occurredAt: dateObj,
				date: date,
				time: time,
				duration: (() => {
					const hasPreferredSeconds =
						typeof data.durationSeconds === "number" &&
						Number.isFinite(data.durationSeconds) &&
						data.durationSeconds >= 0;
					const legacySeconds = Number(data.duration);
					const seconds = hasPreferredSeconds
						? data.durationSeconds
						: legacySeconds;
					if (Number.isFinite(seconds)) {
						return seconds < 60
							? `${seconds}s`
							: `${Math.floor(seconds / 60)}:${(seconds % 60)
									.toString()
									.padStart(2, "0")}`;
					}
					return data.duration || "0s";
				})(),
				durationSeconds:
					typeof data.durationSeconds === "number" &&
					Number.isFinite(data.durationSeconds) &&
					data.durationSeconds >= 0
						? data.durationSeconds
						: undefined,
				status: isCallStatus(data.status) ? data.status : CALL_STATUS.COMPLETED,
				summary: data.summary ?? "",
				// Temporarily empty until merged
				actionItems: [],
				transcript: generatedTranscript,
				transcriptFeedback: data.transcriptFeedback ?? null,
				transcriptEdit,
				collectedNumbers: Array.isArray(data.collectedNumbers)
					? data.collectedNumbers
					: [],
				title: data.title ?? "",
				gcsUri: data.gcsUri,
				notes: data.notes,
				tagIds: Array.isArray(data.tagIds)
					? data.tagIds
							.map(normalizeTagValue)
							.filter((tagId): tagId is string => tagId !== null)
					: [],
				interactionStatus: data.interactionStatus,
				isTestCall: !!data.isTestCall,
				isBlocked: !!data.isBlocked,
				outboundPlaybookRunId:
					typeof data.outboundPlaybookRunId === "string"
						? data.outboundPlaybookRunId
						: null,
				outboundTaskId:
					typeof data.outboundTaskId === "string" ? data.outboundTaskId : null,
				outboundTaskAttemptId:
					typeof data.outboundTaskAttemptId === "string"
						? data.outboundTaskAttemptId
						: null,
				outboundPlaybookId:
					typeof data.outboundPlaybookId === "string"
						? data.outboundPlaybookId
						: null,
				outboundPlaybookVersion:
					typeof data.outboundPlaybookVersion === "number"
						? data.outboundPlaybookVersion
						: null,
			} as Call;
		};

		const buildPendingCall = (doc: {
			id: string;
			data: () => DocumentData;
		}): Call => {
			const data = doc.data();
			const occurredAt =
				data.occurredAt?.toDate?.() || data.createdAt?.toDate?.() || new Date();
			return {
				id: doc.id,
				piiRemovalPending: true,
				manualResolutionRequired: data.manualResolutionRequired === true,
				caller: { name: "", phone: "" },
				agent: "",
				occurredAt,
				date: formatLocalDateKey(occurredAt),
				time: `${String(occurredAt.getHours()).padStart(2, "0")}:${String(occurredAt.getMinutes()).padStart(2, "0")}`,
				duration: "0s",
				status: CALL_STATUS.PROCESSING,
				caseId: typeof data.caseId === "number" ? data.caseId : undefined,
				direction: data.direction,
				isOutbound: data.isOutbound === true,
			};
		};

		const buildCallSearchDocument = (call: Call): SearchDocument => ({
			id: call.id,
			type: "call",
			summary: call.summary,
			transcript: call.transcript,
			notes: call.notes,
			title: call.title,
			name: [call.caller.name, call.caller.phone, call.agent]
				.filter(Boolean)
				.join(" "),
		});

		const buildCallerSearchDocument = (caller: Caller): SearchDocument => {
			const callerId = normalizePhone(caller.phoneNumber);
			return {
				id: callerId,
				type: "caller",
				name: [caller.name, caller.phoneNumber, caller.email]
					.filter(Boolean)
					.join(" "),
				notes: [caller.notes, caller.tags?.join(" ")].filter(Boolean).join(" "),
			};
		};

		const buildActionItemSearchDocument = (
			actionItem: ActionItem,
		): SearchDocument => {
			const action = actionItem.action as Partial<
				import("@repo/common").FollowUpActionDetails &
					Omit<import("@repo/common").BookingActionDetails, "type">
			>;
			const actionText = [
				action?.details,
				action?.accommodationType,
				action?.customerName,
				action?.phoneNumber,
				action?.email,
				action?.agent,
				action?.bookingDate,
			]
				.filter(Boolean)
				.join(" ");

			return {
				id: actionItem.id!,
				type: "actionItem",
				title:
					action?.details || action?.accommodationType || action?.customerName,
				summary: actionText,
				notes: [actionItem.notes, actionItem.tags?.join(" ")]
					.filter(Boolean)
					.join(" "),
			};
		};

		const mergeAndSetCalls = () => {
			const releasedIds = new Set(localReleasedCalls.map((call) => call.id));
			const localCalls = [
				...localReleasedCalls,
				...localPendingCalls.filter((call) => !releasedIds.has(call.id)),
			];
			// Create a map for fast caller lookup by normalized phone
			const callersMap = new Map<string, Caller>();
			localCallers.forEach((c) => {
				// Ensure we map by the specialized distinct key (normalized phone)
				// We assume c.phoneNumber is already normalized if created via our UI/DB helpers
				// But strictly speaking, we matched document ID = normalized phone.
				// Storing in map by phoneNumber field is safer.
				if (c.phoneNumber) {
					// Normalize just in case, though it should be clean
					callersMap.set(c.phoneNumber.replace(/\s+/g, ""), c);
				}
			});

			const mergedCalls = localCalls.map((call) => {
				if (call.piiRemovalPending) return call;
				const callActionItems = localActionItems.filter(
					(item) => item.callSid === call.id,
				);

				// Enroll caller info from memory
				// The call.caller.phone derived from 'from' might be formatted or E.164.
				// We should try to match normalized versions.
				const rawFrom = call.caller.phone || "";
				const normalizedFrom = rawFrom.replace(/\s+/g, "");

				const knownCaller = callersMap.get(normalizedFrom);

				const updatedCaller = { ...call.caller };
				if (knownCaller) {
					const manualCallerName =
						typeof knownCaller.name === "string" && knownCaller.name.trim()
							? knownCaller.name.trim()
							: null;
					if (manualCallerName) {
						updatedCaller.name = manualCallerName;
					}
					updatedCaller.email = knownCaller.email ?? updatedCaller.email;
					updatedCaller.tags = knownCaller.tags;
					updatedCaller.notes = knownCaller.notes ?? undefined;
				}

				return {
					...call,
					caller: updatedCaller,
					actionItems: callActionItems,
				};
			});
			// TODO(Backfill): Remove this in-memory sort and revert the query to `orderBy("occurredAt", "desc")`
			// once the backfill script for `occurredAt` has been fully applied to production.
			mergedCalls.sort((a, b) => {
				const aTime = a.occurredAt?.getTime() ?? new Date(a.date).getTime();
				const bTime = b.occurredAt?.getTime() ?? new Date(b.date).getTime();
				return bTime - aTime;
			});
			setCalls(mergedCalls);
		};

		const unsubscribeCalls = onSnapshot(
			qCalls,
			(snapshot) => {
				snapshot.docChanges().forEach((change) => {
					if (change.type === "removed") {
						removeDocument(change.doc.id);
						return;
					}

					const call = buildCall(change.doc);
					addOrUpdateDocument(buildCallSearchDocument(call));
				});

				localReleasedCalls = snapshot.docs.map((doc) => buildCall(doc));
				mergeAndSetCalls();
				releasedCallsReady = true;
				updateCallsLoadState();
			},
			(error) => {
				console.error(`Error fetching calls:`, error);
				Sentry.captureException(error);
				callsListenerFailed = true;
				updateCallsLoadState();
			},
		);

		const pendingCallsRef = collection(
			db,
			"companies",
			user.companyId,
			"pendingPiiCalls",
		);
		const unsubscribePendingCalls = onSnapshot(
			query(pendingCallsRef, orderBy("createdAt", "desc")),
			(snapshot) => {
				localPendingCalls = snapshot.docs.map(buildPendingCall);
				mergeAndSetCalls();
				pendingCallsReady = true;
				updateCallsLoadState();
			},
			(error) => {
				console.error("Error fetching pending PII calls:", error);
				Sentry.captureException(error);
				callsListenerFailed = true;
				updateCallsLoadState();
			},
		);

		const unsubscribeActionItems = onSnapshot(
			qActionItems,
			(snapshot) => {
				snapshot.docChanges().forEach((change) => {
					if (change.type === "removed") {
						removeDocument(change.doc.id);
						return;
					}

					const actionItem = {
						...change.doc.data(),
						id: change.doc.id,
					} as ActionItem;
					addOrUpdateDocument(buildActionItemSearchDocument(actionItem));
				});

				localActionItems = snapshot.docs.map((doc) => {
					return { ...doc.data(), id: doc.id } as ActionItem;
				});
				setActionItems(localActionItems);
				mergeAndSetCalls();
			},
			(error) => {
				console.error(`Error fetching action items:`, error);
				Sentry.captureException(error);
			},
		);

		const unsubscribeCallers = onSnapshot(
			qCallers,
			(snapshot) => {
				snapshot.docChanges().forEach((change) => {
					const caller = { ...change.doc.data(), id: change.doc.id } as Caller;
					const callerId = normalizePhone(caller.phoneNumber);
					if (change.type === "removed") {
						removeDocument(callerId);
						return;
					}

					addOrUpdateDocument(buildCallerSearchDocument(caller));
				});

				localCallers = snapshot.docs.map((doc) => {
					return { ...doc.data(), id: doc.id } as Caller;
				});
				mergeAndSetCalls();
			},
			(error) => {
				console.error(`Error fetching callers:`, error);
				Sentry.captureException(error);
			},
		);

		const unsubscribeAgents = onSnapshot(
			qAgents,
			(snapshot) => {
				const fetchedAgents: Array<{
					agent: Agent;
					stagingDeployment?: Agent["stagingDeployment"];
					liveDeployment?: Agent["liveDeployment"];
				}> = [];
				snapshot.forEach((doc) => {
					const data = doc.data() as Record<string, unknown>;
					fetchedAgents.push({
						agent: {
							...(data as unknown as Agent),
							id: doc.id,
							companyId: user.companyId!,
							phoneNumbers: Array.isArray(data.phoneNumbers)
								? (data.phoneNumbers as string[])
								: data.phoneNumber
									? [String(data.phoneNumber)]
									: [],
							realtime:
								data.realtime && typeof data.realtime === "object"
									? {
											provider:
												typeof (data.realtime as Record<string, unknown>)
													.provider === "string"
													? String(
															(data.realtime as Record<string, unknown>)
																.provider,
														)
													: undefined,
											model:
												typeof (data.realtime as Record<string, unknown>)
													.model === "string"
													? String(
															(data.realtime as Record<string, unknown>).model,
														)
													: undefined,
											effort:
												typeof (data.realtime as Record<string, unknown>)
													.effort === "string"
													? (data.realtime as NonNullable<Agent["realtime"]>)
															.effort
													: undefined,
										}
									: undefined,
							service: null,
						},
						stagingDeployment: normalizeDeploymentSummary(
							data.stagingDeployment,
						),
						liveDeployment: normalizeDeploymentSummary(data.liveDeployment),
					});
				});
				setAgents((prev) => {
					const prevMap = new Map(prev.map((agent) => [agent.id, agent]));
					return fetchedAgents.map(
						({ agent, stagingDeployment, liveDeployment }) => {
							const existing = prevMap.get(agent.id);
							return {
								...agent,
								stagingDeployment:
									stagingDeployment || existing?.stagingDeployment,
								liveDeployment: liveDeployment || existing?.liveDeployment,
								liveDeploymentVersions:
									agent.liveDeploymentVersions ||
									existing?.liveDeploymentVersions ||
									[],
							};
						},
					);
				});
			},
			(error) => {
				console.error("Error fetching agents:", error);
				Sentry.captureException(error);
			},
		);

		const unsubscribeServices = onSnapshot(
			qServices,
			(snapshot) => {
				const fetchedServices = snapshot.docs.map((serviceDoc) => {
					const data = serviceDoc.data() as Record<string, unknown>;
					return {
						...(data as unknown as Service),
						id: serviceDoc.id,
						companyId: user.companyId!,
						description: String(data.description || ""),
						website: String(data.website || ""),
						serviceBrief: String(data.serviceBrief || ""),
						contactDetails: String(data.contactDetails || ""),
						faq: String(data.faq || ""),
						notifications: normalizeServiceNotifications(data.notifications),
						knowledgeBase: String(data.knowledgeBase || ""),
						additionalResources: Array.isArray(data.additionalResources)
							? data.additionalResources
							: [],
						tags: Array.isArray(data.tags) ? data.tags : [],
					} as Service;
				});

				setServices(fetchedServices);
			},
			(error) => {
				console.error("Error fetching services:", error);
				Sentry.captureException(error);
			},
		);

		return trackAccountListenerCleanup(() => {
			unsubscribeCalls();
			unsubscribePendingCalls();
			unsubscribeActionItems();
			unsubscribeCallers();
			unsubscribeAgents();
			unsubscribeServices();
		});
	}, [user?.companyId, user?.uid, trackAccountListenerCleanup]);

	// Listener keys can overlap across accounts and companies. Clear every cached
	// subscription before an account switch so an agent ID (including the
	// reserved `_company` bucket) cannot retain the previous account's data.
	useEffect(() => {
		if (!user?.companyId || !user.uid) return;
		const unsubMap = dailyStatsUnsubscribersRef.current;
		const docsMap = dailyStatsDocsRef.current;
		return () => {
			unsubMap.forEach((unsubscribe) => unsubscribe());
			unsubMap.clear();
			docsMap.clear();
			setDailyCallStats([]);
		};
	}, [user?.companyId, user?.uid]);

	// callStatsDaily subscription — keyed on agents list
	useEffect(() => {
		if (!user?.companyId || !user.uid) return;

		const companyId = user.companyId;
		const unsubMap = dailyStatsUnsubscribersRef.current;

		// Build cutoff date: 365 days ago as YYYY-MM-DD
		const cutoffDate = new Date();
		cutoffDate.setDate(cutoffDate.getDate() - 365);
		const cutoffKey = [
			cutoffDate.getFullYear(),
			String(cutoffDate.getMonth() + 1).padStart(2, "0"),
			String(cutoffDate.getDate()).padStart(2, "0"),
		].join("-");

		const activeAgentIds = new Set([
			...agents.map((agent) => agent.id),
			COMPANY_DAILY_STATS_BUCKET_ID,
		]);

		// Tear down listeners for agents no longer in the list
		unsubMap.forEach((unsub, agentId) => {
			if (!activeAgentIds.has(agentId)) {
				unsub();
				unsubMap.delete(agentId);
				dailyStatsDocsRef.current.delete(agentId);
			}
		});

		const rebuildDailyStats = () => {
			const all: DailyCallStats[] = [];
			dailyStatsDocsRef.current.forEach((docs) => all.push(...docs));
			setDailyCallStats(all);
		};

		// Subscribe to each agent plus the reserved company-level bucket.
		for (const agentId of activeAgentIds) {
			if (unsubMap.has(agentId)) continue; // already subscribed

			const statsRef = collection(
				db,
				"companies",
				companyId,
				"agents",
				agentId,
				"callStatsDaily",
			);
			const statsQuery = query(
				statsRef,
				where("dateKey", ">=", cutoffKey),
				orderBy("dateKey", "desc"),
			);

			const unsub = onSnapshot(
				statsQuery,
				(snapshot) => {
					dailyStatsDocsRef.current.set(
						agentId,
						snapshot.docs.map((d) => d.data() as DailyCallStats),
					);
					rebuildDailyStats();
				},
				(err) => {
					console.error(`callStatsDaily error for bucket ${agentId}:`, err);
				},
			);

			unsubMap.set(agentId, unsub);
		}
	}, [user?.companyId, user?.uid, agents]);

	// Tag listeners belong to the signed-in user, even when the next user has
	// the same company ID. Release them before a different account can reuse them.
	useEffect(() => {
		if (!user?.uid || !user?.companyId) return;
		return clearServiceTagSubscriptions;
	}, [user?.uid, user?.companyId, clearServiceTagSubscriptions]);

	// Service tags subscription — one listener per service subcollection
	useEffect(() => {
		if (!user?.companyId || !user.uid) return;
		const companyId = user.companyId;
		const unsubMap = serviceTagsUnsubscribersRef.current;
		const activeServiceIds = new Set(services.map((s) => s.id));

		// Tear down listeners for services no longer present
		unsubMap.forEach((unsub, serviceId) => {
			if (!activeServiceIds.has(serviceId)) {
				unsub();
				unsubMap.delete(serviceId);
				serviceTagsDocsRef.current.delete(serviceId);
			}
		});

		const rebuildServiceTags = () => {
			const all: ServiceTag[] = [];
			serviceTagsDocsRef.current.forEach((tags) => all.push(...tags));
			setServiceTags(all);
		};

		for (const service of services) {
			if (unsubMap.has(service.id)) continue;

			const tagsRef = serviceTagsCollection(db, companyId, service.id);
			const unsub = onSnapshot(
				tagsRef,
				(snapshot) => {
					serviceTagsDocsRef.current.set(
						service.id,
						snapshot.docs
							.map((d) => ({ ...d.data(), id: d.id }) as ServiceTag)
							.filter((tag) => !tag.deletedAt),
					);
					rebuildServiceTags();
				},
				(err) => {
					console.error(`serviceTags error for service ${service.id}:`, err);
				},
			);
			unsubMap.set(service.id, unsub);
		}
	}, [user?.companyId, user?.uid, services]);

	const deploymentAgentIdsKey = JSON.stringify(
		agents.map((agent) => agent.id).sort(),
	);
	useEffect(() => {
		if (!user?.companyId || !user.uid) return;

		const activeIds = new Set<string>(JSON.parse(deploymentAgentIdsKey));
		const unsubscribeMap = deploymentUnsubscribersRef.current;

		const removeUnsubscribers = (agentId: string) => {
			const existing = unsubscribeMap.get(agentId);
			if (existing?.staging) existing.staging();
			if (existing?.production) existing.production();
			unsubscribeMap.delete(agentId);
		};

		unsubscribeMap.forEach((_, agentId) => {
			if (!activeIds.has(agentId)) {
				removeUnsubscribers(agentId);
			}
		});

		const subscribeToDeployment = (
			agentId: string,
			targetBranch: "staging" | "production",
		) => {
			const deploymentsRef = collection(
				db,
				"companies",
				user.companyId!,
				"agents",
				agentId,
				"deployments",
			);
			const deploymentQuery = query(
				deploymentsRef,
				where("targetBranch", "==", targetBranch),
				orderBy("createdAt", "desc"),
				limit(1),
			);

			return onSnapshot(
				deploymentQuery,
				(snapshot) => {
					const docSnap = snapshot.docs[0];
					const deployment = docSnap
						? ({
								id: docSnap.id,
								...(docSnap.data() as {
									createdAt?: Timestamp;
									status?: DeploymentStatus;
									updatedAt?: Timestamp;
									serviceUpdatedAt?: Timestamp;
									version?: number;
								}),
								createdAt: docSnap.data().createdAt?.toDate?.().getTime(),
								updatedAt: docSnap.data().updatedAt?.toDate?.().getTime(),
								serviceUpdatedAt:
									docSnap.data().serviceUpdatedAt?.toDate?.().getTime() ?? null,
							} as Agent["stagingDeployment"])
						: undefined;

					let liveVersionToPersist: number | undefined;

					setAgents((prev) =>
						prev.map((agent) => {
							if (agentId !== agentId) return agent;
							if (targetBranch === "staging") {
								if (
									agent.stagingDeployment?.id === deployment?.id &&
									agent.stagingDeployment?.createdAt ===
										deployment?.createdAt &&
									agent.stagingDeployment?.status === deployment?.status &&
									agent.stagingDeployment?.updatedAt ===
										deployment?.updatedAt &&
									agent.stagingDeployment?.serviceId ===
										deployment?.serviceId &&
									agent.stagingDeployment?.serviceUpdatedAt ===
										deployment?.serviceUpdatedAt &&
									agent.stagingDeployment?.version === deployment?.version &&
									agent.stagingDeployment?.prUrl === deployment?.prUrl &&
									agent.stagingDeployment?.actionUrl ===
										deployment?.actionUrl &&
									agent.stagingDeployment?.artifactId ===
										deployment?.artifactId &&
									agent.stagingDeployment?.artifactUrl ===
										deployment?.artifactUrl &&
									agent.stagingDeployment?.action === deployment?.action &&
									agent.stagingDeployment?.error === deployment?.error &&
									agent.stagingDeployment?.rollbackToVersion ===
										deployment?.rollbackToVersion
								) {
									return agent;
								}
								return { ...agent, stagingDeployment: deployment };
							}

							let liveDeploymentVersions = agent.liveDeploymentVersions;
							// Wait for final deploy_success before appending to the live versions history list
							if (
								deployment?.status === "deploy_success" &&
								typeof deployment.version === "number" &&
								!Number.isNaN(deployment.version)
							) {
								const existingVersions = liveDeploymentVersions || [];
								if (!existingVersions.includes(deployment.version)) {
									liveVersionToPersist = deployment.version;
									liveDeploymentVersions = [
										...existingVersions,
										deployment.version,
									];
								}
							}

							if (
								agent.liveDeployment?.id === deployment?.id &&
								agent.liveDeployment?.createdAt === deployment?.createdAt &&
								agent.liveDeployment?.status === deployment?.status &&
								agent.liveDeployment?.updatedAt === deployment?.updatedAt &&
								agent.liveDeployment?.version === deployment?.version &&
								agent.liveDeployment?.prUrl === deployment?.prUrl &&
								agent.liveDeployment?.actionUrl === deployment?.actionUrl &&
								agent.liveDeployment?.artifactId === deployment?.artifactId &&
								agent.liveDeployment?.artifactUrl === deployment?.artifactUrl &&
								agent.liveDeployment?.action === deployment?.action &&
								agent.liveDeployment?.error === deployment?.error &&
								agent.liveDeployment?.rollbackToVersion ===
									deployment?.rollbackToVersion &&
								agent.liveDeploymentVersions === liveDeploymentVersions
							) {
								return agent;
							}
							return {
								...agent,
								liveDeployment: deployment,
								liveDeploymentVersions,
							};
						}),
					);

					if (
						targetBranch === "production" &&
						liveVersionToPersist !== undefined
					) {
						const agentRef = doc(
							db,
							"companies",
							user.companyId!,
							"agents",
							agentId,
						);
						setDoc(
							agentRef,
							{ liveDeploymentVersions: arrayUnion(liveVersionToPersist) },
							{ merge: true },
						).catch((error) => {
							console.error(
								"Error saving live deployment version history:",
								error,
							);
							Sentry.captureException(error);
						});
					}
				},
				(error) => {
					console.error(
						`Error listening to ${targetBranch} deployments:`,
						error,
					);
					Sentry.captureException(error);
				},
			);
		};

		activeIds.forEach((agentId) => {
			if (unsubscribeMap.has(agentId)) return;
			const stagingUnsub = subscribeToDeployment(agentId, "staging");
			const productionUnsub = subscribeToDeployment(agentId, "production");
			unsubscribeMap.set(agentId, {
				staging: stagingUnsub,
				production: productionUnsub,
			});
		});

		return () => {
			unsubscribeMap.forEach((_, agentId) => removeUnsubscribers(agentId));
		};
	}, [deploymentAgentIdsKey, user?.companyId, user?.uid]);

	const login = async (email: string, password: string) => {
		await signInWithEmailAndPassword(auth, email, password);
	};

	const logout = async () => {
		if (user?.uid) {
			logAction(db, {
				userId: user.uid,
				type: "user_logout",
				companyId: user.companyId!,
			}).catch((err) => console.error("Failed to log logout", err));
		}
		clearAccountData();
		setUser(null);
		try {
			await signOut(auth);
		} catch (error) {
			setUser(user);
			throw error;
		}
	};

	// User Profile Update
	const updateUserProfile = async (data: Partial<User>) => {
		if (!user?.companyId || !user?.uid) return;

		const userProfileRef = doc(
			db,
			"companies",
			user.companyId,
			"users",
			user.uid,
		);

		// Map our internal User type to the Firestore document structure
		// We only allow updating name and phoneNumber from the UI currently
		const updateData: Record<string, unknown> = {};
		if (data.displayName !== undefined) updateData.name = data.displayName;
		if (data.phoneNumber !== undefined)
			updateData.phoneNumber = data.phoneNumber;
		if (data.language !== undefined) updateData.language = data.language;

		// We use setDoc with merge: true to handle case where user doc doesn't exist
		await setDoc(userProfileRef, updateData, { merge: true });
	};

	const updateCompanyProfile = async (data: Partial<Company>) => {
		if (!user?.companyId || !user.uid) return;
		const companyRef = doc(db, "companies", user.companyId);
		await setDoc(companyRef, data, { merge: true });

		logAction(db, {
			userId: user.uid,
			type: "company_updated",
			companyId: user.companyId!,
			data: {
				updates: data,
			},
		}).catch((err) => console.error("Failed to log company update", err));
	};

	const updateCompanyUser = async (userId: string, data: Partial<User>) => {
		if (!user?.companyId || !user.uid) return;
		const updateFn = httpsCallable(functions, "updateUserForCompany2");
		await updateFn({
			userId,
			companyId: user.companyId!,
			updates: data,
		});
	};

	const deleteCompanyUser = async (userId: string) => {
		if (!user?.companyId || !user.uid) return;
		const deleteFn = httpsCallable(functions, "deleteUserForCompany2");
		await deleteFn({
			userId,
			companyId: user.companyId!,
		});
	};

	// Agent methods
	const updateAgent = async (agent: Agent) => {
		if (!user?.companyId || !user.uid) return;
		try {
			const {
				stagingDeployment: _stagingDeployment,
				liveDeployment: _liveDeployment,
				...agentData
			} = agent;
			const updatedAgent = {
				...agentData,
				hasUncommittedChanges: true,
				updatedAt: serverTimestamp(),
			};
			const dataForWrite = Object.fromEntries(
				Object.entries(updatedAgent).filter(([, value]) => value !== undefined),
			);
			delete (dataForWrite as Partial<Agent>).service;
			const agentRef = doc(db, "companies", user.companyId, "agents", agent.id);

			await runTransaction(db, async (transaction) => {
				const agentDoc = await transaction.get(agentRef);
				if (agentDoc.exists()) {
					const currentData = agentDoc.data();
					const currentUpdatedAt = currentData?.updatedAt;
					const clientUpdatedAt = agent.updatedAt;
					const toMillis = (value: unknown): number | null => {
						if (!value) return null;
						if (
							typeof (value as { toMillis?: unknown }).toMillis === "function"
						)
							return (value as { toMillis: () => number }).toMillis();
						const parsed = new Date(value as Date | string | number).getTime();
						return Number.isNaN(parsed) ? null : parsed;
					};

					const serverTime = toMillis(currentUpdatedAt);
					const clientTime = toMillis(clientUpdatedAt);

					if (serverTime !== null && clientTime === null) {
						throw new Error(
							"Stale agent configuration. Another user has saved newer changes. Please refresh and try again.",
						);
					}

					if (
						serverTime !== null &&
						clientTime !== null &&
						serverTime > clientTime
					) {
						throw new Error(
							"Stale agent configuration. Another user has saved newer changes. Please refresh and try again.",
						);
					}
				}
				transaction.set(agentRef, dataForWrite, { merge: true });
			});

			logAction(db, {
				userId: user.uid,
				type: "agent_updated",
				companyId: user.companyId!,
				agentId: agent.id,
				data: {
					agentName: agent.name,
				},
			}).catch((err) => console.error("Failed to log agent update", err));
		} catch (error) {
			console.error("Error updating agent:", error);
			Sentry.captureException(error);
			throw error;
		}
	};

	const createAgent = async (agent: Agent) => {
		if (!user?.companyId || !user.uid) return;
		try {
			// we use the agent.id from the client side if provided (it usually is `agent-${Date.now()}`)
			const agentRef = doc(db, "companies", user.companyId, "agents", agent.id);
			const { service: _service, ...agentData } = agent;
			await setDoc(agentRef, {
				...agentData,
				createdAt: Timestamp.now(),
				updatedAt: serverTimestamp(),
				companyId: user.companyId!,
			});

			logAction(db, {
				userId: user.uid,
				type: "agent_created",
				companyId: user.companyId!,
				agentId: agent.id,
				data: {
					agentName: agent.name,
				},
			}).catch((err) => console.error("Failed to log agent creation", err));
		} catch (error) {
			console.error("Error creating agent:", error);
			Sentry.captureException(error);
			throw error;
		}
	};

	const updateService = async (service: Service) => {
		if (!user?.companyId || !service.id) return;
		try {
			const serviceRef = doc(
				db,
				"companies",
				user.companyId!,
				"services",
				service.id,
			);
			await setDoc(
				serviceRef,
				{
					...service,
					companyId: user.companyId!,
					updatedAt: serverTimestamp(),
				},
				{ merge: true },
			);

			logAction(db, {
				userId: user.uid,
				type: "service_updated",
				companyId: user.companyId!,
				serviceId: service.id,
				data: {
					serviceName: service.name,
				},
			}).catch((err) => console.error("Failed to log service update", err));
		} catch (error) {
			console.error("Error updating service:", error);
			Sentry.captureException(error);
			throw error;
		}
	};

	const createService = async (service: Service) => {
		if (!user?.companyId || !user.uid) return;
		try {
			await createServiceDocument(db, {
				...service,
				companyId: user.companyId!,
				additionalResources: service.additionalResources || [],
				updatedAt: serverTimestamp(),
			});

			logAction(db, {
				userId: user.uid,
				type: "service_created",
				companyId: user.companyId!,
				serviceId: service.id,
				data: {
					serviceName: service.name,
				},
			}).catch((err) => console.error("Failed to log service creation", err));
		} catch (error) {
			console.error("Error creating service:", error);
			Sentry.captureException(error);
			throw error;
		}
	};

	const deleteService = async (serviceId: string) => {
		if (!user?.companyId || !user.uid) return;
		try {
			await deleteServiceDocument(db, user.companyId, serviceId);

			logAction(db, {
				userId: user.uid,
				type: "service_deleted",
				companyId: user.companyId!,
				serviceId: serviceId,
			}).catch((err) => console.error("Failed to log service deletion", err));
		} catch (error) {
			console.error("Error deleting service:", error);
			Sentry.captureException(error);
			throw error;
		}
	};

	const deleteAgent = async (agentId: string) => {
		if (!user?.companyId || !user.uid) return;
		try {
			const agentRef = doc(db, "companies", user.companyId, "agents", agentId);
			await removeDocument(agentId); // Remove from search index
			await setDoc(agentRef, { deleted: true }, { merge: true }); // Soft delete or hard delete?
			// Actually let's hard delete for now based on requirement "deleteAgent (Real)"
			// But wait, existing code uses deleteDoc usually.
			// Let's use deleteDoc from firestore.
			const { deleteDoc } = await import("firebase/firestore");
			await deleteDoc(agentRef);

			logAction(db, {
				userId: user.uid,
				type: "agent_deleted",
				companyId: user.companyId!,
				agentId: agentId,
			}).catch((err) => console.error("Failed to log agent deletion", err));
		} catch (error) {
			console.error("Error deleting agent:", error);
			Sentry.captureException(error);
		}
	};

	const addPhoneNumber = async (agentId: string, phoneNumber: string) => {
		if (!user?.companyId || !user.uid) return;
		const agent = agents.find((a) => a.id === agentId);
		if (agent) {
			const currentPhones = agent.phoneNumbers || [];
			if (!currentPhones.includes(phoneNumber)) {
				await updateAgent({
					...agent,
					phoneNumbers: [...currentPhones, phoneNumber],
				});

				logAction(db, {
					userId: user.uid,
					type: "phone_number_assigned",
					companyId: user.companyId!,
					agentId: agentId,
					phoneNumber: phoneNumber,
				}).catch((err) => console.error("Failed to log phone assignment", err));
			}
		}
	};

	const removePhoneNumber = async (agentId: string, phoneNumber: string) => {
		if (!user?.companyId || !user.uid) return;
		const agent = agents.find((a) => a.id === agentId);
		if (agent) {
			const currentPhones = agent.phoneNumbers || [];
			await updateAgent({
				...agent,
				phoneNumbers: currentPhones.filter((p) => p !== phoneNumber),
			});

			logAction(db, {
				userId: user.uid,
				type: "phone_number_unassigned",
				companyId: user.companyId!,
				agentId: agentId,
				phoneNumber: phoneNumber,
			}).catch((err) => console.error("Failed to log phone unassignment", err));
		}
	};

	const promoteToTesting = async (agentId: string) => {
		if (!user?.companyId || !user.uid) return;
		try {
			const createStagingPr = httpsCallable(functions, "createStagingPr");
			await createStagingPr({ agentId, companyId: user.companyId });
			setAgents((prev) =>
				prev.map((agent) =>
					agent.id === agentId
						? { ...agent, hasUncommittedChanges: false }
						: agent,
				),
			);
		} catch (error) {
			console.error("Error promoting agent to testing:", error);
			Sentry.captureException(error);
			throw error;
		}
	};

	const promoteToLive = async (agentId: string) => {
		if (!user?.companyId || !user.uid) return;
		try {
			const createProductionPr = httpsCallable(functions, "createProductionPr");
			await createProductionPr({ agentId, companyId: user.companyId });
		} catch (error) {
			console.error("Error promoting agent to live:", error);
			Sentry.captureException(error);
			throw error;
		}
	};

	const rollbackLive = async (agentId: string, targetVersion?: number) => {
		if (!user?.companyId || !user.uid) return;
		try {
			const createProductionRollbackPr = httpsCallable(
				functions,
				"createProductionRollbackPr",
			);
			await createProductionRollbackPr({
				agentId,
				companyId: user.companyId!,
				targetVersion,
			});
		} catch (error) {
			console.error("Error rolling back agent live deployment:", error);
			Sentry.captureException(error);
			throw error;
		}
	};

	const previewGenerateAgentFiles = async (agentId: string) => {
		if (!user?.companyId || !user.uid)
			throw new Error("Missing company context");
		try {
			const previewGenerate = httpsCallable(
				functions,
				"previewGenerateAgentFiles",
			);
			const result = await previewGenerate({
				agentId,
				companyId: user.companyId!,
			});
			return result.data as PreviewGenerateAgentFilesResult;
		} catch (error) {
			console.error("Error generating preview agent files:", error);
			Sentry.captureException(error);
			const firebaseError = error as {
				message?: string;
				code?: string;
				details?: unknown;
			};
			const detailsReason =
				typeof firebaseError?.details === "object" &&
				firebaseError.details !== null &&
				"reason" in firebaseError.details &&
				typeof (firebaseError.details as { reason?: unknown }).reason ===
					"string"
					? (firebaseError.details as { reason: string }).reason
					: null;
			const message =
				detailsReason ??
				firebaseError?.message ??
				"Failed to generate local preview files.";
			throw new Error(message);
		}
	};

	const refreshUser = async () => {
		if (auth.currentUser) {
			await auth.currentUser.getIdTokenResult(true);
			window.location.reload();
		}
	};

	// Derived
	const dashboardRecentCalls = useMemo(() => {
		// Just map first 3 calls
		return calls.slice(0, 3);
	}, [calls]);

	const hydratedAgents = useMemo(
		() =>
			agents.map((agent) => ({
				...agent,
				service:
					services.find((service) => service.id === agent.serviceId) || null,
			})),
		[agents, services],
	);

	return (
		<DataContext.Provider
			value={{
				user,
				isLoggedIn: !!user,
				login,
				logout,
				loading,
				language,
				setLanguage,
				agents: hydratedAgents,
				testingPhoneNumbers,
				services,
				serviceTags,
				calls,
				callsLoading,
				callsError,
				dashboardRecentCalls,
				actionItems,
				auditLogs,
				dailyCallStats,
				companyProfile,
				subscription,
				users,
				updateCompanyProfile,
				updateCompanyUser,
				deleteCompanyUser,
				updateAgent,
				createAgent,
				updateService,
				createService,
				deleteService,
				deleteAgent,
				addPhoneNumber,
				removePhoneNumber,
				updateUserProfile,
				refreshUser,
				promoteToTesting,
				promoteToLive,
				rollbackLive,
				previewGenerateAgentFiles,
			}}
		>
			{children}
		</DataContext.Provider>
	);
}

export function useData() {
	const context = useContext(DataContext);
	if (context === undefined) {
		throw new Error("useData must be used within a DataProvider");
	}
	return context;
}

// Export empty/default values if needed for other files that might import them
// But ideally we should remove those imports from other files.
